import React, { Component, lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';

import { baseRoutes } from '../../constants/routes.constants';
import { usePermissions } from '../../hooks/usePermissions';
import { RootState } from '../../store';
import type { Permission } from '../../constants/rbac';
import { AUTH_RELOAD, AUTH_SIGN_OUT, AUTH_WAITING_LOAD_FAILED } from '../../constants/auth.strings';

// Loaded only when shown: it brings the Firebase calls (resend, "I've confirmed it", sign-out) that no granted page
// needs from here.
const AccessPending = lazy(() => import('../../pages/AccessPending'));

// When the waiting page's code cannot be loaded (a dropped connection), say so with a way forward rather than leave a
// blank page: Reload, or Sign out (the one sign-out, loaded on the click; since item 4 the header has no account menu).
// Nothing of the guarded page is shown either way.
class WaitingPageLoadFailed extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div role="alert" style={{ padding: '2rem', textAlign: 'center' }}>
        {AUTH_WAITING_LOAD_FAILED}{' '}
        <button type="button" onClick={() => window.location.reload()}>
          {AUTH_RELOAD}
        </button>{' '}
        <button
          type="button"
          onClick={async () => {
            try {
              const { signOut } = await import('../../services/signOut');
              await signOut();
            } catch {
              window.location.reload();
            }
          }}
        >
          {AUTH_SIGN_OUT}
        </button>
      </div>
    );
  }
}

interface RequirePermissionProps {
  permission: Permission;
  /** Where an authenticated-but-denied user lands. Defaults to the home page. */
  redirectTo?: string;
  children: React.ReactNode;
}

// The RBAC route guard: authentication AND a matrix permission.
//
// Mirrors SearchEntryAuthGuard's structure deliberately, including the
// render-null-before-initialized step: until Firebase has resolved the session
// (and delivered the token whose claims carry the role), rendering anything
// else produces a redirect flash on a hard reload for a user who is actually
// allowed. Null first, decide once the claims have landed.
//
// FAIL CLOSED: a signed-in user whose token carries no role claim, or an
// unknown role string, holds no permission and is denied like anyone else.
const RequirePermission: React.FC<RequirePermissionProps> = ({
  permission,
  redirectTo = baseRoutes.root,
  children,
}) => {
  const location = useLocation();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { isReady, hasPermission, role, tenantId, getAccessLevel } = usePermissions();

  if (!isReady) {
    return null;
  }

  if (!isAuthenticated) {
    // Same destination and shape as SearchEntryAuthGuard, with the actual
    // origin recorded so a future sign-in flow can return the user here.
    return <Navigate to={baseRoutes.signIn} replace state={{ from: location.pathname }} />;
  }

  // THE WAITING PAGE (27 Oct, step 7), in place of the page, for a signed-in account that cannot use any page yet:
  //   - no role at all (a new sign-up, or an unknown role string: fail closed, it is null);
  //   - a role that holds this permission only for its own company ('own') but has no company (no tenant), which the
  //     gateway refuses (403) on every call.
  // It shows nothing of the page and makes no data call (pages/AccessPending). A role that holds the permission at
  // 'none' is redirected as before.
  if (role === null || (getAccessLevel(permission) === 'own' && !tenantId)) {
    return (
      <WaitingPageLoadFailed>
        <Suspense fallback={null}>
          <AccessPending />
        </Suspense>
      </WaitingPageLoadFailed>
    );
  }

  if (!hasPermission(permission)) {
    if (location.pathname === redirectTo) {
      // The denial destination itself is RBAC-guarded (the home page requires
      // search.own), so a user denied THERE cannot be sent there again -
      // Navigate-to-self loops forever. Rendering nothing is the terminal
      // fail-closed state for a user no page will accept.
      return null;
    }

    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

/**
 * ANY ROLE (28 Oct, the sign-in build, Joey's fix to batch 5+6+8): the page opens for every signed-in account that has a
 * role, whatever its permissions and whether or not it has a company; the page gates its own parts. Used by My profile,
 * where every signed-in person finds Change password and Log out (a Content Manager holds no licence.read, and an
 * account with no company could only reach the waiting page). No role at all: the waiting page, as RequirePermission
 * shows it (it has Sign out and Change password). The same null-before-ready and sign-in redirect as above.
 */
export const RequireAnyRole: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { isReady, role } = usePermissions();

  if (!isReady) {
    return null;
  }
  if (!isAuthenticated) {
    return <Navigate to={baseRoutes.signIn} replace state={{ from: location.pathname }} />;
  }
  if (role === null) {
    return (
      <WaitingPageLoadFailed>
        <Suspense fallback={null}>
          <AccessPending />
        </Suspense>
      </WaitingPageLoadFailed>
    );
  }
  return <>{children}</>;
};

export default RequirePermission;
